import { Settings } from '#~/common/card/index.js';
import { ProductionDialog } from '#~/room/components/production/production.js';
import {
  getErrorMessage,
  getRecordingConfig
} from '#~/room/components/production/recording/utils.js';
import { useEgressErrorStore } from '#~/store/index.js';
import { StatusEgressState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { Cell, Table } from '../layout/index.js';
import type { Props } from './types.js';
import { useDialog } from '@studio/design-system/dialog';
import { clsx } from 'cn';
import { Fragment, type ReactElement } from 'react';
import { twMerge } from 'cn';
import { DurationTimer } from '#~/room/components/production/components/source-card.js';

/** `<Recording>` component. */
export const Recording = ({ className, sources, ...props }: Props): ReactElement => {
  const { open } = useDialog(ProductionDialog);
  const { isDismissed } = useEgressErrorStore();

  return (
    <div className={twMerge(clsx(className, 'flex flex-col gap-8'))} {...props}>
      {sources.map((source) => {
        const config = getRecordingConfig(source);
        const status =
          source.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE
            ? 'active'
            : 'error';
        const errorMsg = getErrorMessage(
          source.egress?.recordingStatus?.state,
          source.egress?.recordingStatus?.error
        );
        const visibleError = isDismissed(source.id, 'recording', errorMsg) ? undefined : errorMsg;

        return (
          <Fragment key={source.id}>
            <Table className='grow'>
              <thead>
                <th colSpan={config.length - 1}>
                  <div className='flex items-center gap-4'>
                    <span
                      className={twMerge(
                        status === 'active' ? 'text-success' : 'text-error',
                        'mr-4'
                      )}
                    >
                      {'\u23FA'}
                    </span>
                    <span
                      className={twMerge('font-medium', status !== 'error' && 'text-secondary')}
                    >
                      {source.name}
                    </span>
                  </div>
                </th>
                <th>
                  <Settings
                    className='w-18 opacity-100'
                    icon='settings'
                    onClick={(): void => {
                      open({ defaultTab: 'recording' });
                    }}
                  />
                </th>
              </thead>
              <tbody>
                <tr>
                  {config.map(({ name, value, duration }) =>
                    duration ? (
                      <Cell key={name} value={<DurationTimer duration={duration} />} label={name} />
                    ) : (
                      <Cell key={name} value={status === 'error' ? '-' : value!} label={name} />
                    )
                  )}
                </tr>
              </tbody>
            </Table>
            {visibleError && (
              <p className='flex items-center gap-6 rounded-6 border-1 border-error p-12'>
                <i className='i-ion-warning-outline text-lg text-error' />
                <span className='text-sm'>{visibleError}</span>
              </p>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
